import i18next from 'i18next'
import React from 'react'
import { Fun, IOWidget, any, async, fromJSX, nothing } from 'widgets-for-react'
import { DataLayer } from '../../../../utils/datalayer'
import { ComponentDidMount } from '../../../../utils/lifecycleMethods'
import { ModalButtonProps, ModalLayout } from '../../../../utils/modal'
import { collectionEditorWidget } from '../../collections/collectionEditor/collectionEditorWidget'
import { CollectionsLoaderState } from '../../collections/collectionsLoader/collectionsLoaderState'
import { isSavedIn } from '../../collections/collectionsLoader/collectionsLoaderUtils'
import { RootLayout } from '../../rootLayout'
import { routeUpdaters } from '../../routes/routesState'
import { ComponentSaverLayout } from './componentSaverLayout'
import {
  ComponentSaverState,
  componentSaverStateUpdaters,
} from './componentSaverState'

export const componentSaverWidget =
  (
    collectionsState: CollectionsLoaderState,
    componentName?: string
  ): IOWidget<
    ComponentSaverState,
    Fun<
      ComponentSaverState,
      ComponentSaverState & { reloadCollections?: boolean }
    >
  > =>
  (componentSaverState) =>
    any([
      async(componentSaverState.savingState).map(
        componentSaverStateUpdaters.savingStateUpdater
      ),
      async(componentSaverState.deletingState).map(
        componentSaverStateUpdaters.deletingStateUpdater
      ),
      componentSaverState.collectionCreatorState.opened
        ? collectionEditorWidget(true)(
            componentSaverState.collectionCreatorState
          ).map((update) =>
            componentSaverStateUpdaters.collectionCreatorStateUpdater(update)
          )
        : componentSaverState.opened
        ? fromJSX((setState) => {
            const {
              deletingState,
              deSelectedCollections,
              savingState,
              selectedCollections,
            } = componentSaverState
            const onHide = () => {
              setState(componentSaverStateUpdaters.stopSavingUpdater)
              DataLayer.cancelSaveToCollection()
            }
            if (collectionsState.kind === 'loaded') {
              const collections = collectionsState.value
              if (
                savingState.kind === 'loading' ||
                deletingState.kind === 'loading' ||
                (savingState.kind === 'unloaded' &&
                  deletingState.kind === 'unloaded')
              ) {
                const createCollectionButtonProps: ModalButtonProps = {
                  kind: 'button',
                  type: 'submit',
                  onClick: () => {
                    setState(
                      componentSaverStateUpdaters.startCreateCollectionUpdater
                    )
                    if (componentName !== undefined)
                      DataLayer.saveToNewCollection(componentName)
                  },
                  label: i18next.t('collection:createNewCollection'),
                  isDisabled:
                    savingState.kind === 'loading' ||
                    deletingState.kind === 'loading',
                  iconId: 'hexagon-sliced-open-bold',
                  iconSize: '24',
                }
                return (
                  <ModalLayout
                    title={i18next.t(
                      'collection:modalTitleSaveInCollection'
                    )}
                    onHide={onHide}
                    hasForm={collections.length > 0}
                    primaryAction={
                      collections.length > 0
                        ? {
                            kind: 'button',
                            type: 'submit',
                            label: i18next.t('common:add'),
                            onClick: () => {
                              setState(
                                componentSaverStateUpdaters.startLoadingSavingDeletingStateUpdater
                              )
                              if (componentName) {
                                DataLayer.saveToExistingCollection(
                                  componentName
                                )
                              }
                            },
                            isDisabled:
                              savingState.kind === 'loading' ||
                              deletingState.kind === 'loading' ||
                              (selectedCollections.count() <= 0 &&
                                deSelectedCollections.count() <= 0),
                          }
                        : createCollectionButtonProps
                    }
                    secondaryAction={
                      collections.length > 0
                        ? createCollectionButtonProps
                        : undefined
                    }
                  >
                    {collections.length > 0 ? (
                      <ComponentSaverLayout.MultipleCollections>
                        {collections.map((collection) => {
                          const isInCollection = isSavedIn(
                            collection,
                            componentSaverState.componentId
                          )
                          return (
                            <ComponentSaverLayout.CollectionSelector
                              name={collection.Name}
                              id={collection.Id}
                              latestComponents={(collection.Components.length >
                              3
                                ? collection.Components.slice(0, 3)
                                : collection.Components
                              ).map((component) => component.Name)}
                              isChecked={
                                isInCollection
                                  ? !deSelectedCollections.includes(
                                      collection.Id
                                    )
                                  : selectedCollections.includes(collection.Id)
                              }
                              onChange={() =>
                                setState(
                                  isInCollection
                                    ? componentSaverStateUpdaters.deSelectedCollectionsUpdater(
                                        collection.Id
                                      )
                                    : componentSaverStateUpdaters.selectedCollectionsUpdater(
                                        collection.Id
                                      )
                                )
                              }
                            />
                          )
                        })}
                      </ComponentSaverLayout.MultipleCollections>
                    ) : (
                      <ComponentSaverLayout.EmptyCollections />
                    )}
                  </ModalLayout>
                )
              } else
                return (
                  <ModalLayout
                    title={i18next.t(
                      'collection:modalTitleSaveInCollection'
                    )}
                    onHide={onHide}
                    primaryAction={{
                      kind: 'button',
                      type: 'button',
                      label: i18next.t('common:close'),
                      onClick: () =>
                        setState(componentSaverStateUpdaters.stopSavingUpdater),
                    }}
                  >
                    {savingState.kind === 'loaded' &&
                    deletingState.kind === 'loaded' ? (
                      <>
                        <ComponentDidMount
                          onMount={() =>
                            DataLayer.successfulSaveToCollection(componentName)
                          }
                        />
                        <ComponentSaverLayout.Succes
                          savedCollections={selectedCollections
                            .flatMap<{ Id: string; Name: string }>(
                              (collectionId) => {
                                const collection = collections.find(
                                  (collection) => collection.Id === collectionId
                                )
                                return collection === undefined
                                  ? []
                                  : [collection]
                              }
                            )
                            .toArray()}
                          deletedCollections={deSelectedCollections
                            .flatMap<{ Id: string; Name: string }>(
                              (collectionId) => {
                                const collection = collections.find(
                                  (collection) => collection.Id === collectionId
                                )
                                return collection === undefined
                                  ? []
                                  : [collection]
                              }
                            )
                            .toArray()}
                        />
                      </>
                    ) : (
                      <ComponentSaverLayout.SaveDeleteFailed />
                    )}
                  </ModalLayout>
                )
            } else
              return collectionsState.kind === 'unloaded' ? (
                <ModalLayout
                  title={i18next.t(
                    'collection:modalTitleCreateAccount'
                  )}
                  onHide={onHide}
                  primaryAction={{
                    label: i18next.t('subscription:startYourFreeTrial'),
                    kind: 'link',
                    to:
                      routeUpdaters(i18next.language).register.url +
                      '?originPath=' +
                      window.location.pathname,
                  }}
                  secondaryAction={{
                    label: i18next.t('auth:loginHeader'),
                    kind: 'link',
                    to:
                      routeUpdaters(i18next.language).login.url +
                      `?originPath=${window.location.pathname}`,
                  }}
                >
                  <ComponentSaverLayout.CreateAccount />
                </ModalLayout>
              ) : (
                <ModalLayout
                  title={i18next.t(
                    'collection:modalTitleSaveInCollection'
                  )}
                  onHide={onHide}
                >
                  {collectionsState.kind === 'loading' ? (
                    <RootLayout.Loader hasWrapper showText />
                  ) : (
                    <ComponentSaverLayout.CollectionsLoadingFailed />
                  )}
                </ModalLayout>
              )
          })
        : nothing(),
    ])
